import PropTypes from 'prop-types'
import React from 'react'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { esES } from '@mui/x-date-pickers/locales'
import esLocale from 'date-fns/locale/es'
import formatDate from 'date-fns/format'
import Error from '../Error'
import clsx from 'clsx'
import CalendarIcon from '@mui/icons-material/CalendarToday'
import Hint from '../../atoms/Hint'
import DateFieldToolbar from './DateFieldToolbar'
import { marginSizes } from '../../../configs/sizesConfig'
import { useCustomField } from '../../../hooks/useCustomField'
import OptionalLabel from './OptionalLabel'
const esLocaleText = esES.components.MuiLocalizationProvider.defaultProps.localeText

const sizeMap = {
  '2xl': 'sm:max-w-sm',
  xl: 'sm:max-w-xs'
}

const DateField = ({
  label,
  name,
  placeholder,
  validate,
  containerClassName = '',
  margin = 'normal',
  classNameLabel,
  size = 'xl',
  hint,
  format,
  ariaLabel,
  hideError = false,
  optional,
  ...props
}) => {
  const { input, gotError } = useCustomField(name, { validate })
  return (
    <>
      <label className={clsx('block font-bold mb-2', classNameLabel)} htmlFor={`${name}-input`}>
        {label}
        {optional && <OptionalLabel />}
      </label>
      {hint && <Hint hint={hint} name={name} />}
      <div
        className={clsx(
          'bg-white relative js-field-container border-gray-800 border-2 min-h-12 rounded',
          sizeMap[size],
          containerClassName,
          marginSizes[margin],
          gotError ? 'focus-within:border-red-500 border-red-500' : 'focus-within:border-yellow-500'
        )}
      >
        <div className="absolute h-full w-11.25 bg-blue-400 border-l-2 border-black right-0 pointer-events-none" />
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale} localeText={esLocaleText}>
          <DesktopDatePicker
            value={input.value || null}
            onChange={input.onChange}
            format={format || 'dd/MM/yyyy'}
            dayOfWeekFormatter={date => formatDate(date, 'EEEEEE', { locale: esLocale })}
            closeOnSelect
            slots={{
              toolbar: DateFieldToolbar,
              openPickerIcon: () => <CalendarIcon className="text-blue-700" />
            }}
            slotProps={{
              toolbar: { hidden: false },
              openPickerButton: { 'aria-label': 'Abrir selector de fecha' },
              textField: {
                id: `${name}-input`,
                fullWidth: true,
                variant: 'standard',
                className: 'relative',
                placeholder: placeholder || 'DD/MM/AAAA',
                inputProps: { 'aria-label': placeholder || ariaLabel },
                InputProps: {
                  disableUnderline: true,
                  sx: {
                    padding: '8px',
                    paddingRight: '3.25rem',
                    fontSize: '1rem',
                    minHeight: '2.75rem',
                    boxSizing: 'border-box',
                    '& .MuiInputAdornment-root': {
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      right: 0,
                      width: '2.8125rem',
                      height: 'auto',
                      maxHeight: 'none',
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    },
                    '& .MuiIconButton-root': { padding: 0, margin: 0 }
                  }
                }
              }
            }}
            {...props}
          />
        </LocalizationProvider>
        {!hideError && <Error name={name} className="absolute -bottom-6" />}
      </div>
    </>
  )
}

export default DateField

DateField.propTypes = {
  classNameLabel: PropTypes.string,
  containerClassName: PropTypes.string,
  format: PropTypes.string,
  hideError: PropTypes.bool,
  hint: PropTypes.string,
  label: PropTypes.string,
  margin: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string,
  size: PropTypes.string,
  validate: PropTypes.func,
  optional: PropTypes.bool
}
